if (!customElements.get('marquee-component')) {
  customElements.define(
    'marquee-component',
    class MarqueeComponent extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.wrapper = this.querySelector('[ref="wrapper"]');
        this.content = this.querySelector('[ref="content"]');
        this.marqueeItems = this.querySelectorAll('[ref="marqueeItems[]"]');

        this.speedFactor = parseFloat(this.dataset.speedFactor) || 25;
        this.movementDirection = this.dataset.movementDirection || 'normal';

        // Wait for images and content to load
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
          // Use requestAnimationFrame to ensure layout is complete
          requestAnimationFrame(() => this.init());
        }
      }

      init() {
        if (!this.wrapper || !this.content || this.marqueeItems.length === 0) {
          console.warn('Marquee component: Missing required elements');
          return;
        }

        // Check if user prefers reduced motion
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
          this.dataset.disabled = 'true';
          return;
        }

        // Duplicate content enough times to fill the screen
        this.ensureFill();

        // Calculate and set animation speed
        setTimeout(() => {
          this.calculateSpeed();
        }, 100);

        // Observe resize to recalculate speed
        this.setupResizeObserver();

        // Handle visibility changes to pause/resume animation
        this.setupIntersectionObserver();
      }

      ensureFill() {
        // Check if we need more duplicates to fill the viewport
        const viewportWidth = window.innerWidth;

        // If content is less than 2x viewport width, add more duplicates
        while (this.content.scrollWidth < viewportWidth * 2) {
          const clone = this.marqueeItems[0].cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          this.content.appendChild(clone);
        }
      }

      calculateSpeed() {
        // Get the width of one set of items (first marqueeItems element)
        const itemsWidth = this.marqueeItems[0]?.offsetWidth;

        if (!itemsWidth) {
          console.warn('Marquee: Unable to calculate width');
          return;
        }

        // Calculate duration based on width and speed factor
        // Higher speed factor = faster animation (lower duration)
        const duration = itemsWidth / this.speedFactor;

        // Set CSS custom property for animation duration
        this.style.setProperty('--marquee-speed', `${duration}s`);
      }

      setupResizeObserver() {
        if (!window.ResizeObserver) return;

        this.resizeObserver = new ResizeObserver(() => {
          this.calculateSpeed();
        });

        this.resizeObserver.observe(this.wrapper);
      }

      setupIntersectionObserver() {
        if (!window.IntersectionObserver) return;

        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.content.style.animationPlayState = 'running';
            } else {
              this.content.style.animationPlayState = 'paused';
            }
          });
        });

        this.intersectionObserver.observe(this);
      }

      disconnectedCallback() {
        // Cleanup observers when component is removed
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }
    }
  );
}
